import apiClient from './apiClient';
import { isAdminSession } from '@/utils/adminFreshness';

// Cache constants
const CACHE_DURATION = 3 * 60 * 1000; // 3 minutes
const BANNERS_CACHE_KEY_PREFIX = 'banners';
const BANNERS_TIMESTAMP_KEY_PREFIX = 'bannersTimestamp';

const unwrapList = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
};

// Function to get device-specific cache keys
const getCacheKeys = () => {
    // Check if we're on mobile (using the same breakpoint as in Home.vue)
    const isMobile = window.innerWidth < 992;
    const deviceType = isMobile ? 'mobile' : 'desktop';
    
    return {
        bannersKey: `${BANNERS_CACHE_KEY_PREFIX}_${deviceType}`,
        timestampKey: `${BANNERS_TIMESTAMP_KEY_PREFIX}_${deviceType}`
    };
};

// Helper function to validate cache
const isCacheValid = (timestamp) => {
    if (isAdminSession() || !timestamp) return false;
    const now = Date.now();
    const lastUpdate = parseInt(timestamp);
    return (now - lastUpdate) < CACHE_DURATION;
};

/**
 * Get all banners or banners filtered by position
 * @param {Object} options - Filter options
 * @param {string} options.position - Optional position filter ('home_top', 'products_page', etc.)
 * @param {boolean} options.active - Optional filter for active banners only
 * @param {boolean} options.forceRefresh - Force a refresh of the cache
 * @returns {Promise<Array>} - Array of banner objects
 */
export const getBanners = async ({ position, active, forceRefresh = false } = {}) => {
    // Get device-specific cache keys
    const { bannersKey, timestampKey } = getCacheKeys();
    console.log(`Using cache keys for getBanners: ${bannersKey}, ${timestampKey}`);
    
    // Try to get from cache if not forcing refresh
    if (!forceRefresh) {
        const cachedTimestamp = localStorage.getItem(timestampKey);
        
        // Check if cache is valid
        if (isCacheValid(cachedTimestamp)) {
            const cachedBanners = unwrapList(JSON.parse(localStorage.getItem(bannersKey) || '[]'));
            
            // Apply filters client-side if needed
            let filteredBanners = [...cachedBanners];
            
            if (position) {
                filteredBanners = filteredBanners.filter(banner => banner.position === position);
            }
            
            if (active) {
                const now = new Date();
                filteredBanners = filteredBanners.filter(banner => {
                    const isActive = banner.is_active;
                    const hasStarted = !banner.start_date || new Date(banner.start_date) <= now;
                    const hasNotEnded = !banner.end_date || new Date(banner.end_date) >= now;
                    return isActive && hasStarted && hasNotEnded;
                });
            }
            
            return filteredBanners;
        }
    }
    
    // Build query parameters
    let params = {};
    if (position) params.position = position;
    if (active !== undefined) params.active = active;
    
    try {
        const response = await apiClient.get('/banners/', { params });
        const banners = unwrapList(response.data);
        
        // Cache the full result using device-specific keys
        if (!isAdminSession()) {
            localStorage.setItem(bannersKey, JSON.stringify(banners));
            localStorage.setItem(timestampKey, Date.now().toString());
        }
        
        // Log cache storage
        console.log(`Cached ${banners.length} banners for ${window.innerWidth < 992 ? 'mobile' : 'desktop'} view`);
        
        return banners;
    } catch (error) {
        console.error('Error fetching banners:', error);
        throw error;
    }
};

/**
 * Get a single banner by ID
 * @param {number} id - Banner ID
 * @returns {Promise<Object>} - Banner object
 */
export const getBannerById = async (id) => {
    try {
        const response = await apiClient.get(`/banners/${id}/`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching banner with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Upload a banner image to Bunny Storage
 * @param {File} image - Image file to upload
 * @returns {Promise<string>} - URL of the uploaded image
 */
export const uploadBannerImage = async (image) => {
    const formData = new FormData();
    formData.append('image', image);
    
    try {
        const response = await apiClient.post('/banners/upload_image/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        return response.data.image_url;
    } catch (error) {
        console.error('Error uploading banner image:', error);
        const detail = error.response?.data?.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Error al subir la imagen');
    }
};

/**
 * Create a new banner
 * @param {Object} bannerData - Banner data
 * @returns {Promise<Object>} - Created banner object
 */
export const createBanner = async (bannerData) => {
    try {
        const response = await apiClient.post('/banners/', bannerData);
        
        // Invalidate both desktop and mobile caches
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_mobile`);
        
        return response.data;
    } catch (error) {
        console.error('Error creating banner:', error);
        const data = error.response?.data;
        const detail = data?.detail || data?.image_url?.[0] || data?.non_field_errors?.[0];
        throw new Error(typeof detail === 'string' ? detail : 'Error al crear el banner');
    }
};

/**
 * Update an existing banner
 * @param {number} id - Banner ID
 * @param {Object} bannerData - Updated banner data
 * @returns {Promise<Object>} - Updated banner object
 */
export const updateBanner = async (id, bannerData) => {
    try {
        const response = await apiClient.put(`/banners/${id}/`, bannerData);
        
        // Invalidate both desktop and mobile caches
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_mobile`);
        
        return response.data;
    } catch (error) {
        console.error(`Error updating banner with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Delete a banner
 * @param {number} id - Banner ID
 * @returns {Promise<void>}
 */
export const deleteBanner = async (id) => {
    try {
        await apiClient.delete(`/banners/${id}/`);
        
        // Invalidate both desktop and mobile caches
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_mobile`);
    } catch (error) {
        console.error(`Error deleting banner with ID ${id}:`, error);
        throw error;
    }
};

// CAROUSEL IMAGES API FUNCTIONS
const CAROUSEL_CACHE_KEY_PREFIX = 'carousel_images';
const CAROUSEL_TIMESTAMP_KEY_PREFIX = 'carousel_images_timestamp';

// Function to get device-specific carousel cache keys
const getCarouselCacheKeys = () => {
    // Check if we're on mobile
    const isMobile = window.innerWidth < 992;
    const deviceType = isMobile ? 'mobile' : 'desktop';
    
    return {
        carouselKey: `${CAROUSEL_CACHE_KEY_PREFIX}_${deviceType}`,
        carouselTimestampKey: `${CAROUSEL_TIMESTAMP_KEY_PREFIX}_${deviceType}`
    };
};

/**
 * Get all carousel images - Método simplificado
 * @param {boolean} forceRefresh - Forzar recarga desde servidor
 * @returns {Promise<Array>} - Array of carousel image objects
 */
export const getCarouselImages = async (forceRefresh = false) => {
    // Get device-specific cache keys for carousel
    const { carouselKey, carouselTimestampKey } = getCarouselCacheKeys();
    console.log(`Using carousel cache keys: ${carouselKey}, ${carouselTimestampKey}`);
    
    // Try to get from cache if not forcing refresh
    if (!forceRefresh) {
        const cachedTimestamp = localStorage.getItem(carouselTimestampKey);
        
        if (isCacheValid(cachedTimestamp)) {
            const cachedImages = unwrapList(JSON.parse(localStorage.getItem(carouselKey) || '[]'));
            console.log(`Retrieved ${cachedImages.length} carousel images from ${window.innerWidth < 992 ? 'mobile' : 'desktop'} cache`);
            return cachedImages;
        }
    }
    
    try {
        console.log('Petición a la API para obtener banners del carousel');
        // Petición directa y sencilla
        const response = await apiClient.get('/banners/', { 
            params: { 
                position: 'carousel'
                // No filtrar por active para mostrar todos en el panel de administración
            },
            timeout: 10000 // Tiempo de espera más largo
        });
        
        const banners = unwrapList(response.data);
        console.log('Datos recibidos de la API:', banners);
        if (!isAdminSession()) {
            localStorage.setItem(carouselKey, JSON.stringify(banners));
            localStorage.setItem(carouselTimestampKey, Date.now().toString());
        }
        console.log(`Cached ${banners.length} carousel images for ${window.innerWidth < 992 ? 'mobile' : 'desktop'} view`);
        
        return banners;
    } catch (error) {
        console.error('Error al obtener banners del carousel:', error.response || error);
        if (isAdminSession()) throw error;
        
        // Si hay datos en caché, usarlos como fallback
        const cachedData = localStorage.getItem(carouselKey);
        if (cachedData) {
            console.log(`Usando datos en caché como fallback para ${window.innerWidth < 992 ? 'mobile' : 'desktop'}`);
            return unwrapList(JSON.parse(cachedData));
        }
        
        // Si no hay caché, devolver un array vacío
        console.log('No hay caché, devolviendo array vacío');
        return [];
    }
};

/**
 * Upload a carousel image
 * @param {Object} imageData - Image data including file and metadata
 * @returns {Promise<Object>} - Created carousel image object
 */
export const uploadCarouselImage = async (imageData) => {
    try {
        // First upload the image
        const imageUrl = await uploadBannerImage(imageData.file);
        
        // Then create a banner with position='carousel'
        const carouselData = {
            image_url: imageUrl,
            url_to: imageData.link || '',
            title: imageData.title || '',
            position: 'carousel',
            is_active: true,
            order: imageData.order || 0,
        };

        if (imageData.mobileFile) {
            const mobileImageUrl = await uploadBannerImage(imageData.mobileFile);
            carouselData.mobile_image_url = mobileImageUrl;
        }
        
        const response = await createBanner(carouselData);
        
        // Invalidate both desktop and mobile carousel caches
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_mobile`);
        
        return response;
    } catch (error) {
        console.error('Error uploading carousel image:', error);
        throw error;
    }
};

/**
 * Update an existing carousel image
 * @param {number} id - Carousel image ID
 * @param {Object} imageData - Updated image data
 * @returns {Promise<Object>} - Updated carousel image object
 */
export const updateCarouselImage = async (id, imageData) => {
    try {
        // Update the banner data
        const updateData = {
            url_to: imageData.link || '',
            is_active: true,
            order: imageData.order || 0,
            title: imageData.title || ''
        };
        
        // If there's a new image file, upload it
        if (imageData.file) {
            const imageUrl = await uploadBannerImage(imageData.file);
            updateData.image_url = imageUrl;
        }
        
        // If there's a new mobile image file, upload it
        if (imageData.mobileFile) {
            const mobileImageUrl = await uploadBannerImage(imageData.mobileFile);
            updateData.mobile_image_url = mobileImageUrl;
        }
        
        const response = await updateBanner(id, updateData);
        
        // Invalidate both desktop and mobile carousel caches
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_mobile`);
        
        return response;
    } catch (error) {
        console.error(`Error updating carousel image with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Delete a carousel image
 * @param {number} id - Carousel image ID
 * @returns {Promise<void>}
 */
export const deleteCarouselImage = async (id) => {
    try {
        await deleteBanner(id);
        
        // Invalidate both desktop and mobile carousel caches
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_mobile`);
    } catch (error) {
        console.error(`Error deleting carousel image with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Update the order of two carousel images
 * @param {number} id1 - First image ID
 * @param {number} order1 - New order for first image
 * @param {number} id2 - Second image ID
 * @param {number} order2 - New order for second image
 * @returns {Promise<void>}
 */
/**
 * Helper function to clear all banner caches (both desktop and mobile)
 * Useful for debugging or forced refresh
 */
// export const clearAllBannerCaches = () => {
//     // Clear regular banner caches
//     localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_desktop`);
//     localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_desktop`);
//     localStorage.removeItem(`${BANNERS_CACHE_KEY_PREFIX}_mobile`);
//     localStorage.removeItem(`${BANNERS_TIMESTAMP_KEY_PREFIX}_mobile`);
    
//     // Clear carousel banner caches
//     localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_desktop`);
//     localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_desktop`);
//     localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_mobile`);
//     localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_mobile`);
    
//     // Clear legacy cache keys
//     localStorage.removeItem('banners');
//     localStorage.removeItem('bannersTimestamp');
//     localStorage.removeItem('carousel_images');
//     localStorage.removeItem('carousel_images_timestamp');
    
//     console.log('All banner caches cleared');
// };

export const updateCarouselImageOrder = async (id1, order1, id2, order2) => {
    try {
        // Update first image
        await updateBanner(id1, { order: order1 });
        
        // Update second image
        await updateBanner(id2, { order: order2 });
        
        // Invalidate both desktop and mobile carousel caches
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_desktop`);
        localStorage.removeItem(`${CAROUSEL_CACHE_KEY_PREFIX}_mobile`);
        localStorage.removeItem(`${CAROUSEL_TIMESTAMP_KEY_PREFIX}_mobile`);
    } catch (error) {
        console.error('Error updating carousel image order:', error);
        throw error;
    }
};
